import { memo, useState, useEffect } from "react";
import { Zap, Crown, Star, Flame, Shield, Trash2, Circle, Gem, X } from "lucide-react";
import { canPlayCard, phaseAllowed, evalCondition } from "../../utils/cardUtils";
import { toast } from "sonner";
export { canPlayCard };
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const MODIFIER_LABEL = (card) => {
  const p = card.pitcher_modifier || 0;
  const b = card.batter_modifier || 0;
  const f = card.fielding_modifier || 0;
  if (p !== 0 && p === b && p === f) return `${p > 0 ? "+" : ""}${p} batter, pitcher & fielder`;
  if (p !== 0) return `${p > 0 ? "+" : ""}${p} pitcher`;
  if (b !== 0) return `${b > 0 ? "+" : ""}${b} batter`;
  if (f !== 0) return `${f > 0 ? "+" : ""}${f} fielding`;
  return null;
};


const RARITY_STYLES = {
  junk_wax:  { label: "Junk Wax",  classes: "bg-slate-800 border-slate-700 text-slate-500" },
  common:    { label: "Common",    classes: "bg-slate-700/60 border-slate-600 text-slate-300" },
  uncommon:  { label: "Uncommon",  classes: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" },
  rare:      { label: "Rare",      classes: "bg-amber-500/20 border-amber-500/40 text-amber-400" },
  legendary: { label: "Legendary", classes: "bg-purple-500/20 border-purple-500/40 text-purple-400" },
  exclusive: { label: "Exclusive", classes: "bg-orange-500/20 border-orange-500/40 text-orange-400" },
  iconic:    { label: "Iconic",    classes: "bg-yellow-500/20 border-yellow-500/40 text-yellow-400" },
};
const rarityStyle = (rarity) => RARITY_STYLES[rarity] || { label: rarity || "—", classes: "bg-slate-800 border-slate-700 text-slate-500" };
const RARITY_ICON_MAP = {
  common:    { Icon: Circle, color: "text-slate-500" },
  uncommon:  { Icon: Gem,    color: "text-emerald-400" },
  rare:      { Icon: Star,   color: "text-amber-400" },
  legendary: { Icon: Crown,  color: "text-purple-400" },
  junk_wax:  { Icon: Trash2, color: "text-sky-400" },
  exclusive: { Icon: Shield, color: "text-orange-400" },
  iconic:    { Icon: Flame,  color: "text-yellow-400" },
};
const maxLabel = (v) => v == null || v === 0 ? null : v > 0 ? `max +${v}` : `max ${v}`;

// Drag-and-drop wrapper for a single card slot. Exposes the dnd-kit sortable
// handle on the container so clicking the card button still fires normally —
// the 8px activation distance prevents drags from firing on short taps.
function SortableCardWrapper({ id, className, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  // See SortableChip in PitchingPhase: dnd-kit's role="button" tabIndex={0} makes
  // this wrapper a duplicate, inert button in front of the real card control.
  const { role: _dndRole, tabIndex: _dndTabIndex, ...dragAttrs } = attributes;

  const upDrag = isDragging && transform?.y != null ? Math.max(0, -transform.y) : 0;
  const liftScale = 1 + Math.min(upDrag / 250, 0.22);
  const transformCSS = transform
    ? isDragging
      ? `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${liftScale})`
      : CSS.Transform.toString(transform)
    : undefined;

  return (
    <div
      ref={setNodeRef}
      className={className}
      {...dragAttrs}
      {...listeners}
      style={{
        transform: transformCSS,
        transition: isDragging ? undefined : transition,
        opacity: isDragging ? 0.7 : 1,
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "none",
        zIndex: isDragging ? 10 : undefined,
        transformOrigin: isDragging ? "bottom center" : undefined,
      }}
    >
      {children}
    </div>
  );
}

function ManagerCardHand({
  cards = [],
  playedCardIds = [],
  spentCardIds = [],
  onToggle,
  maxPlays = 4,
  phase = "any",
  gameState = null,
  currentBatter = null,
  currentPitcher = null,
  teamRole = null,
  onInstantPlay = null,
  activeCards = [],
  onDeactivate = null,
  showSuggestions = true,
  activeListeners = [],
  onToggleListener = null,
  oppActiveCards = [],
  lockedCardIds = [],
  gameId = null,
  streakerInteractiveEligible = false,
  unlimitedStacking = false,
  // Render the hand but refuse input. Used during pitch resolution, where the
  // panel previously UNMOUNTED entirely: `[data-testid^="card-"]` returned zero
  // elements for 20+ seconds, so a player (or an agent) had no way to see the
  // hand, and a click landing on the remount was silently swallowed because the
  // element it targeted no longer existed. Keeping the cards mounted keeps
  // positions stable, keeps them readable, and makes refusal explicit.
  readOnly = false,
}) {
  const playedCount = playedCardIds.length;
  const [shakeItOffPending, setShakeItOffPending] = useState(false);
  const [streakerModePending, setStreakerModePending] = useState(false);

  // Streak Yourself eligibility — interactive mode is only offered when the
  // viewer has a keyboard (desktop) AND the game is online H2H or vs-bot.
  // The mode/bot-role part comes from the parent via `streakerInteractiveEligible`;
  // we still check for a keyboard locally so mobile never gets the option.
  const _isDesktop = typeof window !== "undefined"
    && !("ontouchstart" in window)
    && (window.innerWidth ?? 0) >= 720;
  const _streakerInteractiveEligible = streakerInteractiveEligible && _isDesktop;

  // Local sort order — persists to sessionStorage if gameId is provided so the
  // order survives page refreshes within the tab session. sessionStorage avoids
  // the per-game key accumulation that previously bloated localStorage and
  // triggered QuotaExceededError on render.
  const [sortedIds, setSortedIds] = useState(() => {
    if (gameId) {
      try {
        const key = `cardOrder-${gameId}-${teamRole}`;
        const saved = sessionStorage.getItem(key);
        if (saved) return JSON.parse(saved);
      } catch {
        // ignore — fall through to default
      }
    }
    return cards.map(c => c.id);
  });

  // Persist sorted card order. Wrapped so any storage failure (quota, disabled,
  // private mode) fails silently rather than crashing the component.
  useEffect(() => {
    if (!gameId) return;
    try {
      const key = `cardOrder-${gameId}-${teamRole}`;
      sessionStorage.setItem(key, JSON.stringify(sortedIds));
    } catch {
      // ignore — preference persistence is best-effort
    }
  }, [sortedIds, gameId, teamRole]);

  useEffect(() => {
    setSortedIds(prev => {
      const incoming = new Set(cards.map(c => c.id));
      const kept = prev.filter(id => incoming.has(id));
      const added = cards.map(c => c.id).filter(id => !kept.includes(id));
      return [...kept, ...added];
    });
  }, [cards]);
  const sortedCards = sortedIds.map(id => cards.find(c => c.id === id)).filter(Boolean);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setSortedIds(prev => {
      const from = prev.indexOf(String(active.id));
      const to   = prev.indexOf(String(over.id));
      return arrayMove(prev, from, to);
    });
  };

  if (!cards.length) {
    return (
      <div className="text-xs text-slate-600 italic text-center py-2">No cards in hand</div>
    );
  }

  return (
    <div className="h-full flex flex-col justify-center min-w-0">
      <div className="flex flex-row gap-1.5 overflow-x-auto pb-1" data-testid="card-tray" data-role={teamRole} data-phase={phase} data-card-count={sortedCards.length}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortedIds} strategy={horizontalListSortingStrategy}>
            {sortedCards.map((card, idx) => {
              const isSpent = spentCardIds.includes(card.id);
              const isPlayed = playedCardIds.includes(card.id);
              const activeEntry = activeCards.find(a => a && a.id === card.id) || null;
              const isActive = !!activeEntry;
              const isListener = !!card.listener;
              const isArmed = isListener && activeListeners.includes(card.id);
              // isArmedReady: armed, phase matches, AND any condition on the card is currently satisfied.
              //   → full teal glow (will fire on the next eligible event).
              // isArmedWaiting: armed, phase matches, but the condition isn't satisfied yet (e.g. no runners).
              //   → faded green (armed but won't fire until conditions change).
              // isArmedOutOfPhase: armed but wrong phase entirely → faded green.
              const _inPhase = isArmed && phaseAllowed(card.phase, phase);
              const _condMet = !card.condition || evalCondition(card.condition, { gameState, currentBatter, currentPitcher });
              const isArmedReady = _inPhase && _condMet;
              const isArmedWaiting = _inPhase && !_condMet;
              const isArmedOutOfPhase = isArmed && !phaseAllowed(card.phase, phase);
              const isBurned = !!(activeEntry && activeEntry.burned);
              const isLocked = lockedCardIds.includes(card.id);
              const isCurrentlyEffective = isActive && !isBurned && canPlayCard(card, { phase: card.phase || "any", gameState, currentBatter, currentPitcher, teamRole });
              const isDormant = isActive && !isBurned && !isCurrentlyEffective;
              // Listener cards (e.g. Slap Hit) can be armed even when not "playable" by normal rules.
              // They stay in hand, auto-fire when conditions are met, and never get spent.
              const playable = !isSpent && !isActive && !isLocked && canPlayCard(card, { phase, gameState, currentBatter, currentPitcher, teamRole });
              const isInstant = !!card.instant && !!onInstantPlay;
              // Listener arming respects the card's who field just like canPlayCard does —
              // a batting card (e.g. pick_off_to_the_races) can only be armed by the
              // batter, and a pitcher card can only be armed by the pitcher.
              const _listenerWhoOk = !card.who || card.who === "either"
                || ((card.who === "batter" || card.who === "batting") && teamRole === "batter")
                || ((card.who === "pitcher" || card.who === "pitching" || card.who === "fielding") && teamRole === "pitcher");
              const canToggleListener = isListener && !isSpent && !isBurned && !!onToggleListener;
              const isDisabled = isLocked || isSpent || isActive || !playable || (!isInstant && !isPlayed && playedCount >= maxPlays) || (!onInstantPlay && !onToggle);
              const modLabel = MODIFIER_LABEL(card);
              const maxStack = card.max ?? null;

              const isInactive = isSpent || isBurned;
              const cardStateClasses = isInactive
                ? "border-slate-700/40 bg-slate-800/20 opacity-50 cursor-not-allowed"
                : isLocked
                ? "border-orange-700/50 bg-orange-900/10 opacity-60 cursor-not-allowed"
                : isArmedReady
                ? "border-teal-500 bg-teal-500/10 shadow-[0_0_10px_rgba(20,184,166,0.25)] cursor-default"
                : (isArmedWaiting || isArmedOutOfPhase)
                ? "border-green-700/40 bg-green-900/20 opacity-60 cursor-default"
                : isDormant
                ? "border-emerald-600/40 bg-emerald-900/20 cursor-not-allowed"
                : isActive
                ? "border-emerald-500 bg-emerald-500/10 cursor-not-allowed"
                : isPlayed
                ? "border-emerald-500 bg-emerald-500/10 cursor-pointer"
                : (isInstant && playable && showSuggestions)
                ? "border-amber-400 bg-amber-500/15 shadow-[0_0_12px_rgba(245,158,11,0.35)] cursor-pointer"
                : (playable && showSuggestions)
                ? "border-amber-400/70 bg-slate-900/50 cursor-pointer"
                : playable
                ? "border-slate-600/60 bg-slate-900/50 cursor-pointer"
                : "border-slate-700/40 bg-slate-900/40 cursor-not-allowed";

              const nameClass = `text-xs font-black leading-tight ${
                isInactive ? "text-slate-500"
                : isLocked ? "text-orange-500/70"
                : isArmedReady ? "text-teal-300"
                : (isArmedWaiting || isArmedOutOfPhase) ? "text-green-600/60"
                : (isDormant || isActive || isPlayed) ? "text-slate-300"
                : playable ? "text-white"
                : "text-slate-400"
              }`;

              // Why this card can't be played, in priority order. Computed BEFORE
              // handleClick so the click path can surface it.
              //
              // Two bugs fixed here:
              //   * the tail used to fall through to "Hand limit" whenever isDisabled
              //     was true, including when it was true only because no handler was
              //     wired (`!onInstantPlay && !onToggle`) — reporting a limit that
              //     was never hit.
              //   * a card belonging to the other side reported the generic
              //     "Conditions not met", which reads as "your player doesn't qualify"
              //     when the real answer is "wrong half-inning". _listenerWhoOk already
              //     encoded that test but nothing consumed it.
              const _cardSideLabel = (card.who === "batter" || card.who === "batting")
                ? "Batting"
                : (card.who === "pitcher" || card.who === "pitching" || card.who === "fielding")
                ? "Pitching"
                : null;
              const _mySideLabel = teamRole === "batter" ? "batting" : "pitching";
              const _phaseLabel = Array.isArray(card.phase) ? card.phase.join(" or ") : card.phase;
              const _hitHandLimit = !isInstant && !isPlayed && playedCount >= maxPlays;
              const _noHandler = !onInstantPlay && !onToggle;

              const [blockedCode, disabledReason] = isLocked
                ? ["locked", "Locked — shaken off by opponent; reactivate next inning"]
                : isSpent
                ? ["spent", card.global_once ? "Used — once per game card, already played" : "Used — already played this half-inning"]
                : isBurned
                ? ["burned", "Burned — this card was destroyed"]
                : isArmedReady
                ? ["armed", "Armed — will auto-fire when triggered"]
                : isArmedWaiting
                ? ["armed", "Armed — waiting for conditions to be met"]
                : isArmedOutOfPhase
                ? ["armed-out-of-phase", `Armed — won't fire this phase (triggers during ${_phaseLabel} phase)`]
                : isActive
                ? ["active", "Active — in play"]
                : isDormant
                ? ["dormant", "Dormant — in play but conditions aren't met yet"]
                : !playable
                ? (!phaseAllowed(card.phase, phase)
                    ? ["wrong-phase", `Can only play during ${_phaseLabel} phase`]
                    : !_listenerWhoOk && _cardSideLabel
                    ? ["wrong-role", `${_cardSideLabel} card — you're ${_mySideLabel} this half-inning`]
                    : ["conditions", "Conditions not met — check the card's requirement"])
                : _hitHandLimit
                ? ["hand-limit", `Hand limit — max ${maxPlays} card${maxPlays !== 1 ? "s" : ""} per pitch`]
                : _noHandler
                ? ["not-your-turn", "Not playable right now — no action available in this phase"]
                : isDisabled
                ? ["unavailable", "Unavailable right now"]
                : [null, null];

              const handleClick = () => {
                if (isListener) return;
                if (isDisabled) {
                  // Never fail silently. A click that does nothing and says nothing
                  // leaves the player unable to distinguish "wrong role" from
                  // "I missed the hitbox".
                  if (disabledReason) toast.error(disabledReason);
                  return;
                }
                if (isInstant) {
                  if (card.id === "shake_it_off") { setShakeItOffPending(true); return; }
                  // Streaker has two modes: sim (auto-animation) and interactive
                  // (mini-game). Show the picker only when the game / device
                  // supports interactive; otherwise just sim straight away.
                  if (card.id === "streaker") {
                    if (_streakerInteractiveEligible) { setStreakerModePending(true); return; }
                    onInstantPlay?.(card.id, { mode: "sim" });
                    return;
                  }
                  onInstantPlay?.(card.id);
                } else { onToggle?.(card.id); }
              };

              return (
                <SortableCardWrapper key={`${card.id}-${idx}`} id={card.id} className="relative flex-shrink-0 w-[200px] h-[160px]">
                  <div
                    onClick={readOnly
                      ? () => toast("Resolving the pitch — hand locks until it finishes")
                      : handleClick}
                    role="button"
                    tabIndex={(isDisabled || readOnly) ? -1 : 0}
                    onKeyDown={(e) => {
                      if (readOnly) return;
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); }
                    }}
                    title={disabledReason || undefined}
                    aria-disabled={isDisabled || readOnly || undefined}
                    // Full name + rules text on the element itself. The tray is a
                    // horizontally-scrolling row of fixed 200x160 boxes that clip
                    // their contents, so this is the only way to read the whole hand
                    // without scrolling it into view card by card.
                    aria-label={[card.name, card.description, disabledReason && `(${disabledReason})`]
                      .filter(Boolean).join(" — ")}
                    data-testid={`card-${card.id}`}
                    data-card-id={card.id}
                    data-card-name={card.name}
                    data-playable={String(!isDisabled)}
                    data-readonly={readOnly ? "true" : undefined}
                    data-blocked-code={readOnly ? "resolving" : (blockedCode || undefined)}
                    data-blocked-reason={disabledReason || undefined}
                    className={`w-full h-full text-left rounded-md border px-2 py-1.5 transition-all flex flex-col gap-1 overflow-hidden ${cardStateClasses}`}
                  >
                    <div className="flex items-start justify-between gap-1 flex-shrink-0">
                      <p className={nameClass}>{card.name}</p>
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                        {RARITY_ICON_MAP[card.rarity] && (() => {
                          const { Icon, color } = RARITY_ICON_MAP[card.rarity];
                          return <Icon size={11} className={`flex-shrink-0 ${color}`} title={rarityStyle(card.rarity).label} />;
                        })()}
                        {isLocked ? (
                          <span className="text-[9px] font-black text-orange-500/80">LOCKED</span>
                        ) : isInactive ? (
                          <span className="text-[9px] font-black text-slate-500">{isBurned ? "BURNED" : "USED"}</span>
                        ) : isArmedReady ? (
                          <span className="text-[9px] font-black text-teal-400 animate-pulse">ARMED</span>
                        ) : (isArmedWaiting || isArmedOutOfPhase) ? (
                          <span className="text-[9px] font-black text-green-600/70">ARMED</span>
                        ) : isDormant ? (
                          <span className="text-[9px] font-black text-emerald-600/70" title="In play but conditions not met right now">DORMANT</span>
                        ) : (isActive || isPlayed) ? (
                          <span className={`text-[9px] font-black ${isPlayed ? "text-emerald-400 hover:text-emerald-300 transition-colors" : "text-emerald-400/80"}`}>ACTIVE</span>
                        ) : (isInstant && playable && showSuggestions) ? (
                          <span className="text-[9px] font-black text-amber-400">INSTANT</span>
                        ) : card.out_of_turn ? (
                          <span className="text-[9px] font-bold text-purple-400">◉ OOT</span>
                        ) : null}
                      </div>
                    </div>
                    {isActive && !isBurned && card.id === "career_game" && activeEntry?.pinned_batter_name && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-[9px] font-black text-cyan-400 bg-cyan-900/30 border border-cyan-700/40 rounded px-1 py-0 truncate max-w-[140px]" title={activeEntry.pinned_batter_name}>
                          ★ {activeEntry.pinned_batter_name}
                        </span>
                      </div>
                    )}
                    {card.description && (
                      <div className="flex-1 min-h-0 overflow-y-auto">
                        <p className={`text-[11px] leading-snug ${isInactive ? "text-slate-500" : "text-slate-400"}`}>
                          {card.description}
                        </p>
                      </div>
                    )}
                    {isActive && !isBurned && onDeactivate && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeactivate(card.id); }}
                        className="mt-1 self-end flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-400/70 hover:text-red-300 hover:border-red-500/60 hover:bg-red-500/20 transition-colors text-[9px] font-bold"
                        data-testid={`deactivate-${card.id}`}
                        title="Deactivate card"
                      >
                        <X size={8} />
                        Deactivate
                      </button>
                    )}
                    <div className="mt-auto flex items-center gap-1.5 flex-wrap flex-shrink-0">
                      {modLabel && (
                        <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${isInactive ? "text-slate-500" : modLabel.startsWith("-") ? "text-red-400" : "text-amber-400"}`}>
                          <Zap size={10} className="flex-shrink-0" />{modLabel}
                        </span>
                      )}
                      {!unlimitedStacking && maxLabel(maxStack) && (
                        <span className={`text-[11px] font-bold tabular-nums px-1 py-0 rounded ${isInactive ? "text-slate-600 bg-slate-800/40" : "text-slate-400 bg-slate-800/60 border border-slate-700/60"}`}>
                          {maxLabel(maxStack)}
                        </span>
                      )}
                    </div>
                    {canToggleListener && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onToggleListener(card.id); }}
                        className={`mt-1 w-full text-[10px] font-black uppercase tracking-widest rounded py-0.5 transition-all ${
                          isArmedReady
                            ? "bg-teal-500/20 border border-teal-500/50 text-teal-300 hover:bg-teal-500/10 hover:text-teal-400"
                            : (isArmedWaiting || isArmedOutOfPhase)
                            ? "bg-green-900/20 border border-green-700/40 text-green-600/70 hover:bg-green-900/30 hover:border-green-700/60"
                            : "bg-slate-700/40 border border-slate-600/40 text-slate-400 hover:bg-teal-500/15 hover:border-teal-500/40 hover:text-teal-300"
                        }`}
                        title={isArmedReady
                          ? "Click to disarm — card will no longer auto-fire"
                          : isArmedWaiting
                          ? "Click to disarm — armed but waiting for conditions to be met"
                          : isArmedOutOfPhase
                          ? `Click to disarm — armed but won't fire until ${Array.isArray(card.phase) ? card.phase.join(" or ") : card.phase} phase`
                          : "Click to arm — card will auto-fire when its condition is met"}
                      >
                        {isArmed ? "◉ Disarm" : "◎ Arm"}
                      </button>
                    )}
                  </div>
                </SortableCardWrapper>
              );
            })}
          </SortableContext>
        </DndContext>
        {Array.from({ length: Math.max(0, 4 - sortedCards.length) }, (_, i) => (
          <div key={`ghost-${i}`} className="flex-shrink-0 w-[200px] h-[160px] rounded-md border border-dashed border-slate-800/30 bg-slate-900/10" />
        ))}
      </div>

      {/* Shake It Off picker modal — window-bound, internal scroll for long
          opponent active lists. Cancel stays pinned at the bottom of the
          card regardless of list length. */}
      {shakeItOffPending && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3">
          <div className="bg-slate-900 border border-amber-500/40 rounded-xl max-w-sm w-full max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
            <div className="px-4 pt-4 pb-2 flex-shrink-0">
              <p className="text-sm font-black text-white mb-1">Shake It Off</p>
              <p className="text-xs text-slate-400">Choose one opponent card to deactivate. They keep it but can't reactivate until next inning.</p>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-4">
              <div className="flex flex-col gap-2">
                {oppActiveCards.filter(a => a && !a.burned).map(a => (
                  <button
                    key={a.id}
                    onClick={() => {
                      setShakeItOffPending(false);
                      onInstantPlay("shake_it_off", { target_card_id: a.id });
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 hover:border-amber-500/60 transition-all"
                  >
                    <span className="text-xs font-bold text-amber-300">{a.name || a.id}</span>
                  </button>
                ))}
                {oppActiveCards.filter(a => a && !a.burned).length === 0 && (
                  <p className="text-xs text-slate-500 italic text-center py-2">No active opponent cards to target.</p>
                )}
              </div>
            </div>
            <div className="px-4 pt-2 pb-4 flex-shrink-0 border-t border-slate-800/60">
              <button
                onClick={() => setShakeItOffPending(false)}
                className="w-full text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Streaker mode picker — only reachable when interactive is eligible.
          Same window-bound + scrollable pattern as the Shake It Off picker so
          the buttons always fit on screen regardless of viewport size. */}
      {streakerModePending && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3">
          <div className="bg-slate-900 border border-purple-500/40 rounded-xl max-w-sm w-full max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
            <div className="px-4 pt-4 pb-2 flex-shrink-0">
              <p className="text-sm font-black text-white mb-1">Streaker</p>
              <p className="text-xs text-slate-400">How does this one go down?</p>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-4">
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => {
                    setStreakerModePending(false);
                    onInstantPlay?.("streaker", { mode: "sim" });
                  }}
                  className="w-full text-left px-3 py-3 rounded-lg border border-slate-600 bg-slate-800/60 hover:bg-slate-800 hover:border-slate-400 transition-all"
                >
                  <p className="text-xs font-black text-white">Sim it</p>
                  <p className="text-[11px] text-slate-500">Watch security chase them — outcome is rolled.</p>
                </button>
                <button
                  onClick={() => {
                    setStreakerModePending(false);
                    onInstantPlay?.("streaker", { mode: "interactive" });
                  }}
                  className="w-full text-left px-3 py-3 rounded-lg border border-purple-500/50 bg-purple-500/10 hover:bg-purple-500/20 hover:border-purple-400 transition-all"
                >
                  <p className="text-xs font-black text-purple-300">Streak yourself</p>
                  <p className="text-[11px] text-slate-400">Pilot the streaker with arrow keys. 20 s to make the exit. Space to sprint.</p>
                </button>
              </div>
            </div>
            <div className="px-4 pt-2 pb-4 flex-shrink-0 border-t border-slate-800/60">
              <button
                onClick={() => setStreakerModePending(false)}
                className="w-full text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export function CardHoverPreview({ card, rect, isSpent, playable, unlimitedStacking = false }) {
  const modLabel = MODIFIER_LABEL(card);
  const rarity = rarityStyle(card.rarity);
  const POPOVER_W = 215;
  const POPOVER_H_EST = 280;
  const VIEWPORT_PAD = 12;
  const vpW = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vpH = typeof window !== "undefined" ? window.innerHeight : 768;
  const cardCenterX = rect.left + rect.width / 2;
  const left = Math.max(VIEWPORT_PAD, Math.min(vpW - VIEWPORT_PAD - POPOVER_W, cardCenterX - POPOVER_W / 2));
  const spaceAbove = rect.top - VIEWPORT_PAD;
  const spaceBelow = vpH - rect.bottom - VIEWPORT_PAD;
  const positionStyle = spaceAbove >= POPOVER_H_EST
    ? { left: `${left}px`, bottom: `${vpH - rect.top + 8}px` }
    : spaceBelow >= POPOVER_H_EST
      ? { left: `${left}px`, top: `${rect.bottom + 8}px` }
      : spaceAbove >= spaceBelow
        ? { left: `${left}px`, top: `${VIEWPORT_PAD}px`, maxHeight: `${spaceAbove - 4}px` }
        : { left: `${left}px`, top: `${rect.bottom + 8}px`, maxHeight: `${spaceBelow - 4}px` };

  const cardBorder = isSpent
    ? "border-slate-700/40 bg-slate-800/20"
    : playable
      ? "border-amber-500/55 bg-slate-900/90"
      : "border-slate-700/50 bg-slate-900/90";

  return (
    <div
      className={`pointer-events-none fixed z-[1000] rounded-md border shadow-2xl flex flex-col gap-1 px-2 py-2 overflow-y-auto ${cardBorder}`}
      style={{ ...positionStyle, width: `${POPOVER_W}px` }}
    >
      <div className="flex items-start justify-between gap-1">
        <p className={`text-xs font-black leading-tight ${isSpent ? "text-slate-500 line-through" : "text-white"}`}>{card.name}</p>
        <span className={`text-[8px] font-bold uppercase tracking-wider px-1 py-px rounded border flex-shrink-0 ${rarity.classes}`}>{rarity.label}</span>
      </div>
      {card.description && (
        <p className="text-[11px] leading-snug text-slate-300">{card.description}</p>
      )}
      {(modLabel || card.max != null || card.out_of_turn || isSpent || (!playable && !isSpent)) && (
        <div className="flex flex-wrap gap-1 mt-0.5">
          {modLabel && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-amber-400">
              <Zap size={10} className="flex-shrink-0" />{modLabel}
            </span>
          )}
          {!unlimitedStacking && maxLabel(card.max) && (
            <span className="text-[11px] font-bold text-slate-400 bg-slate-800/60 border border-slate-700/60 px-1 rounded">{maxLabel(card.max)}</span>
          )}
          {card.out_of_turn && (
            <span className="text-[11px] font-bold text-purple-400 bg-purple-500/10 border border-purple-500/30 px-1 rounded">OOT</span>
          )}
          {isSpent && (
            <span className="text-[10px] text-slate-500 italic">Used this half-inning</span>
          )}
          {!playable && !isSpent && (
            <span className="text-[10px] text-slate-500 italic">Conditions not met</span>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(ManagerCardHand);
